import axios from 'axios'
import { useQuery } from '@tanstack/react-query'
import useAccount from './useAccount'
import { API_BASE_URL } from '../constants/system'
import { skyProductURL, skyQueryKey } from './socialdash/skyEndpoints'
import type { GroupedMetadata } from '../types/gotypes'

export interface UseRecordMetadataOptions {
  customer: string
  job: string
  enabled?: boolean
  /**
   * A Sky product slug. When given, the request goes through `skyProductURL` and
   * the facets are scoped to that product's campaigns (T1.2/T1.4).
   *
   * Same reasoning as `useEntityFilterSummary`: a hand-rolled
   * `/autocoder/${customer}/${job}/…` path still works while silently bypassing
   * `skyProductScope`, which on a product page means job-wide counts under one
   * department's heading — a 200 with the wrong numbers, the failure mode
   * `skyEndpoints.ts` exists to make impossible.
   */
  product?: string
}

// Fetches the record-metadata facets (distinct keys/values + counts) for
// whichever mount applies: the whole job, or one product's campaigns.
export const useRecordMetadata = ({
  customer,
  job,
  enabled = true,
  product,
}: UseRecordMetadataOptions) => {
  const account = useAccount()
  const url = product
    ? skyProductURL(product, '/pipelines/metadata-summary')
    : `${API_BASE_URL}/autocoder/${customer}/${job}/pipelines/metadata-summary`
  const queryKey = product
    ? skyQueryKey(product, 'record_metadata_summary')
    : (['record_metadata_summary', { customer, job }] as const)

  const query = useQuery({
    queryKey,
    enabled: Boolean((product || (customer && job)) && account.user && enabled),
    staleTime: 30000,
    queryFn: async (): Promise<GroupedMetadata> => {
      const res = await axios.get<GroupedMetadata>(url)
      return res.data
    },
  })
  return {
    metadata: query.data?.metadata ?? {},
    totalRecords: query.data?.total_records ?? 0,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  }
}
